import { type ReactNode } from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { ModalHost, Toaster } from '@noor/ui';
import { modalRegistry } from '@/app/modals';
import { AppErrorBoundary } from './error-boundary';
import { ThemeProvider } from './theme-provider';
import { YmapsProvider } from './ymaps-provider';
import { i18n } from './i18n';

/**
 * Wraps the app in every cross-cutting provider. The query client is created
 * in main.tsx and shared with the router so loaders can prefetch into the same
 * cache the components read from.
 */
export function AppProviders({
  queryClient,
  children,
}: {
  queryClient: QueryClient;
  children: ReactNode;
}) {
  return (
    <AppErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <YmapsProvider>
              {children}
              {/* Single mount point for the registry-driven modal stack. */}
              <ModalHost registry={modalRegistry} />
              {/* App-wide toast host. */}
              <Toaster />
            </YmapsProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </I18nextProvider>
    </AppErrorBoundary>
  );
}
